import { isDefined } from "@clipboard-health/util-ts";
import { useCurrentUserId } from "@src/appV2/ExperimentalHomeHealth/lib/useCurrentUserId";
import {
  useInfiniteGetWorkerDetailsAtWorkplace,
  WorkerCategory,
  type WorkerDetailsAtWorkplaceParams,
} from "@src/appV2/Professionals/api/useGetWorkerDetailsAtWorkplace";
import { useCredentialSharing } from "@src/appV2/Professionals/hooks/useCredentialSharing";
import { isShiftStartingSoon } from "@src/appV2/Professionals/utils/isShiftStartingSoon";
import { areCredentialsMissing } from "@src/appV2/WorkerCredentials/areCredentialsMissing";

const MAX_PAGE_SIZE = 50;

export function useGetMissingCredentialsCount() {
  const workplaceId = useCurrentUserId();
  const queryParameter: WorkerDetailsAtWorkplaceParams = {
    category: WorkerCategory.UPCOMING,
    facilityId: workplaceId ?? "",
    sort: "shifts.nextShift.start",
    pageSize: MAX_PAGE_SIZE,
  };
  const {
    isChartingAndTimeClock,
    isChartingOnly,
    isTimeClockOnly,
    enableOnboardingView,
    shiftStartingSoonThresholdInHours,
  } = useCredentialSharing();

  const { data, hasNextPage, isFetching } = useInfiniteGetWorkerDetailsAtWorkplace(queryParameter, {
    refetchOnWindowFocus: false,
    enabled: isDefined(workplaceId) && enableOnboardingView,
  });
  const workers = data?.pages.flatMap((page) => page.workers) ?? [];
  const workersMissingCredentials = workers.filter((worker) =>
    areCredentialsMissing({
      isChartingAndTimeClock,
      isChartingOnly,
      isTimeClockOnly,
      credentials: worker.credentials,
    })
  );
  const workersMissingCredentialsWithShiftStartingSoon = workersMissingCredentials.filter(
    (worker) =>
      isShiftStartingSoon(shiftStartingSoonThresholdInHours, worker.shifts.nextShift?.start)
  );
  const workersMissingCredentialsWithFirstShiftStartingSoon =
    workersMissingCredentialsWithShiftStartingSoon.filter((worker) => worker.shifts.past === 0);

  const countStartingSoon = workersMissingCredentialsWithShiftStartingSoon.length;
  const countFirstShiftStartingSoon = workersMissingCredentialsWithFirstShiftStartingSoon.length;
  const totalUpcomingCount = workersMissingCredentials.length;

  return {
    countStartingSoon,
    countFirstShiftStartingSoon,
    totalUpcomingCount,
    isFetching,
    hasNextPage:
      Boolean(hasNextPage) &&
      isShiftStartingSoon(
        shiftStartingSoonThresholdInHours,
        workers.at(-1)?.shifts.nextShift?.start
      ),
  };
}
